import React, { useContext, useEffect, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useI18nContext } from '@ecg-internal/js-react-i18n';
import {
  Carousel,
  Icon,
  Link,
  PLACEMENT,
  PopOverContent,
  PopOverTrigger,
  PopOverWrapper,
  StructuredListing,
  TextRegular,
} from '@hz-design-system/web-ui';
import { getURlWithoutJPG } from '@ecg-internal/media-util-js-lib';

import { trackGAEvent } from '../../../utils/gaTracking';
import EnvironmentContext from '../../../contexts/EnvironmentContext';
import formatPrice from '../../FeedsApp/components/Feeds/utils/formatPrice';

import Classes from './ListingItemCarousel.module.scss';
import { TCarouselListingItem } from 'types/TCarousels';

type TListingItemCarouselProps = {
  type: string;
  title: string;
  items: TCarouselListingItem[];
  favoriteAdIdList: string[];
  onFavClickHandler: (item: TCarouselListingItem, isFavorite: boolean) => Promise<void>;
  onErrorHandler?: (erroredItem: TCarouselListingItem) => Promise<void>;
};

const ListingItemCarousel = ({
  type,
  title,
  items,
  favoriteAdIdList,
  onFavClickHandler,
  onErrorHandler = async () => {},
}: TListingItemCarouselProps) => {
  const { userLoggedIn } = useContext(EnvironmentContext);
  const { t } = useI18nContext();
  const transitionNodeRefs = useRef(new Map<string, React.RefObject<HTMLDivElement | null>>());

  const getTransitionNodeRef = (itemId: string) => {
    const existingNodeRef = transitionNodeRefs.current.get(itemId);
    if (existingNodeRef) {
      return existingNodeRef;
    }

    const nodeRef = React.createRef<HTMLDivElement>();
    transitionNodeRefs.current.set(itemId, nodeRef);
    return nodeRef;
  };

  useEffect(() => {
    trackGAEvent('Displayed', `${type}_carousel`);
  }, [type]);

  const onClickHandler = (carouselType: string, index: number) => {
    trackGAEvent('Clicked', carouselType, index);
  };

  const getCarouselTitle = () => {
    if (type !== 'PROXIMITY_DEALERS') {
      return title;
    }

    return (
      <div className={Classes.carouselTitle}>
        {title}

        <PopOverWrapper placement={PLACEMENT.BOTTOM}>
          <PopOverTrigger>
            <button className={Classes.infoButton}>
              <Icon name="info" color="text-secondary" />
            </button>
          </PopOverTrigger>
          <PopOverContent>
            <TextRegular>
              {t('proximityDealerCarouselDisclaimer.text')}
              <br />
              <Link
                href="https://help.marktplaats.nl/s/article/rangschikking-advertenties"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('proximityDealerCarouselDisclaimer.link')}
              </Link>
            </TextRegular>
          </PopOverContent>
        </PopOverWrapper>
      </div>
    ) as any;
  };

  return (
    <Carousel title={getCarouselTitle()} id={type} headerClassName="PageContent-offset Carousel-title">
      <TransitionGroup component={null}>
        {items.map((item, index) => {
          const nodeRef = getTransitionNodeRef(item.itemId);

          return (
            <CSSTransition
              key={item.itemId}
              nodeRef={nodeRef}
              timeout={400}
              classNames="item"
              onExited={() => transitionNodeRefs.current.delete(item.itemId)}
            >
              <div ref={nodeRef} className={Classes.item}>
                <StructuredListing
                  linkProps={{ href: item.url, onClick: () => onClickHandler(type, index) }}
                  title={item.title}
                  price={item.formattedPrice ? item.price : formatPrice(item.price, t, true, item.isPricePerMonth)}
                  imageSrc={getURlWithoutJPG(item.imageSrc)}
                  imageLoading={index <= 1 ? 'eager' : 'lazy'}
                  decoding="async"
                  onError={() => onErrorHandler(item)}
                  isFavorite={favoriteAdIdList.includes(item.itemId)}
                  showFav={Boolean(userLoggedIn)}
                  onHeart={() => onFavClickHandler(item, favoriteAdIdList.includes(item.itemId))}
                  badges={item.expired ? { errorText: t('cars.carousels.status.expired') } : undefined}
                />
              </div>
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </Carousel>
  );
};

export default ListingItemCarousel;
